/**
 * @import { FeatureCollection, LineString } from 'geojson'
 * @import { Cruise } from '@/features/itinerary-map/types/cruise'
 * @import { QueryFunctionContext } from '@tanstack/react-query'
 */

import { config } from '@/config/config';
import { cruiseConfig } from '@/config/cruise';
import { api } from '@/lib/api-client';
import { checkErrorCode } from '@/utils/api';
import { FetchError, fetchWithCheck } from '@/utils/fetch';
import { useQueries, useQuery } from '@tanstack/react-query';

/**
 * @typedef {object} CruiseResponse
 * @property {number} errorCode
 * @property {Cruise} data
 */

const path = `cruises/${config.office}/${config.currencyCode}`;

const emptyCruiseRoute = {
  type: 'FeatureCollection',
  features: [],
};

/**
 * @param {string} id
 * @param {number} year
 * @param {boolean} reverse
 */
function queryCruise(id, year, reverse) {
  return {
    queryKey: [path, id, year, reverse],
    queryFn: async (/** @type {QueryFunctionContext} */ { signal }) => {
      const params = new URLSearchParams({
        year: year.toString(),
        reverse: reverse.toString(),
      });
      const response = await api.get(`${path}/${id}?${params}`, { signal });
      const content = /** @type {CruiseResponse} */ (await response.json());
      checkErrorCode(content);
      return content.data;
    },
    staleTime: Infinity,
  };
}

/**
 * @param {string} id
 * @param {number} year
 * @param {boolean} reverse
 */
export function useCruise(id, year, reverse) {
  return useQuery(queryCruise(id, year, reverse));
}

/**
 * @param {string} id
 * @param {number} year
 * @param {boolean} reverse
 * @param {string} routeName
 */
export function useCruiseWithRoute(id, year, reverse, routeName) {
  return useQueries({
    queries: [
      {
        queryKey: ['cruise-route', routeName],
        queryFn: async ({ signal }) => {
          const response = await fetchWithCheck(`${cruiseConfig.routesBaseUrl}${routeName}`, {
            headers: { Accept: 'application/json' },
            signal,
          });
          return /** @type {FeatureCollection<LineString>} */ (await response.json());
        },
        retry: (failureCount, error) => {
          // Don't retry 404 paths
          if (error instanceof FetchError && error.status === 404) {
            return false;
          }

          // Otherwise, retry up to 1 time
          return failureCount < 1;
        },
        staleTime: Infinity,
      },
      queryCruise(id, year, reverse),
    ],
    combine: (results) => {
      const [routeResult, cruiseResult] = results;
      const routeData = routeResult.error ? emptyCruiseRoute : routeResult.data;

      if (routeData && cruiseResult.data) {
        return {
          paths: routeData,
          cruise: cruiseResult.data,
        };
      }

      // Data is not yet available
      return null;
    },
  });
}
